import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, BookOpen, CheckSquare, Square, RotateCcw, Save, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function authHdr(token) {
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export function TopicAllocationTab({ school, token }) {
  const grades   = [...(school.grade_range    || [])].sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
  const subjects = school.subject_whitelist || [];
  const schoolBoard = school.board || "";

  const [allocations,       setAllocations]       = useState({});    // "G5|Mathematics" → { allowed_topics }
  const [selectedGrade,     setSelectedGrade]     = useState(grades[0] || null);
  const [selectedSubject,   setSelectedSubject]   = useState(subjects[0] || null);
  const [availableBoards,   setAvailableBoards]   = useState([]);
  const [selectedBoard,     setSelectedBoard]     = useState(schoolBoard);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [checkedTopics,     setCheckedTopics]     = useState(new Set());
  const [loadingTopics,     setLoadingTopics]     = useState(false);
  const [saving,            setSaving]            = useState(false);
  const [dirty,             setDirty]             = useState(false);

  // ── Load all allocations for this school ──────────────────────────────────
  const loadAllocations = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/api/admin/cr/schools/${school.school_code}/topic-allocation`, {
        headers: authHdr(token),
      });
      const data = await res.json();
      const map  = {};
      (data.allocations || []).forEach(a => {
        map[`${a.grade}|${a.subject}`] = a;
      });
      setAllocations(map);
    } catch {
      toast.error("Failed to load allocations");
    }
  }, [school.school_code, token]);

  useEffect(() => { loadAllocations(); }, [loadAllocations]);

  // ── Load available boards + default back to the school's own board ───────
  useEffect(() => {
    if (!selectedGrade || !selectedSubject) return;
    const gradeNum = parseInt(selectedGrade.replace("G", ""), 10);
    setSelectedBoard(schoolBoard);
    fetch(`${API}/api/subjects/${encodeURIComponent(selectedSubject)}/boards?grade=${gradeNum}`)
      .then(r => r.json())
      .then(boards => setAvailableBoards(boards || []))
      .catch(() => setAvailableBoards([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrade, selectedSubject]);

  // ── Load available chapters when grade + subject + board changes ─────────
  useEffect(() => {
    if (!selectedGrade || !selectedSubject) return;
    const gradeNum = parseInt(selectedGrade.replace("G", ""), 10);
    setLoadingTopics(true);
    setAvailableChapters([]);
    setDirty(false);

    const boardQS = selectedBoard ? `&board=${encodeURIComponent(selectedBoard)}` : "";
    fetch(`${API}/api/subjects/${encodeURIComponent(selectedSubject)}/chapters?grade=${gradeNum}${boardQS}`)
      .then(r => r.json())
      .then(chapters => {
        const names = (chapters || []).map(c => c.chapter);
        setAvailableChapters(names);
        const key  = `${selectedGrade}|${selectedSubject}`;
        const alloc = allocations[key];
        setCheckedTopics(alloc ? new Set(alloc.allowed_topics) : new Set(names));
      })
      .catch(() => toast.error("Failed to fetch chapters"))
      .finally(() => setLoadingTopics(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrade, selectedSubject, selectedBoard, allocations]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggle = (topic) => {
    setCheckedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic); else next.add(topic);
      return next;
    });
    setDirty(true);
  };

  // Select All / Deselect All only ever touch the currently visible board's
  // chapters — selections made on other boards are left untouched.
  const selectAll = () => {
    setCheckedTopics(prev => new Set([...prev, ...availableChapters]));
    setDirty(true);
  };
  const deselectAll = () => {
    setCheckedTopics(prev => {
      const next = new Set(prev);
      availableChapters.forEach(t => next.delete(t));
      return next;
    });
    setDirty(true);
  };

  const visibleCheckedCount = availableChapters.filter(c => checkedTopics.has(c)).length;

  const isRestricted = (grade, subject) => {
    const key = `${grade}|${subject}`;
    return key in allocations;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      // Only treat "everything checked" as a full reset when viewing all boards —
      // a single board's chapters being fully checked doesn't mean other boards are.
      const allSelected = !selectedBoard && checkedTopics.size === availableChapters.length;

      if (allSelected) {
        // Remove restriction — revert to "show all"
        await fetch(
          `${API}/api/admin/cr/schools/${school.school_code}/topic-allocation?grade=${selectedGrade}&subject=${encodeURIComponent(selectedSubject)}`,
          { method: "DELETE", headers: authHdr(token) },
        );
        toast.success("Reset to all topics — no restriction active");
      } else {
        await fetch(`${API}/api/admin/cr/schools/${school.school_code}/topic-allocation`, {
          method: "PUT",
          headers: authHdr(token),
          body: JSON.stringify({
            grade:          selectedGrade,
            subject:        selectedSubject,
            allowed_topics: [...checkedTopics],
          }),
        });
        toast.success(`Saved — ${checkedTopics.size} chapter${checkedTopics.size !== 1 ? "s" : ""} allocated across all boards`);
      }

      await loadAllocations();
      setDirty(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="topic-allocation-tab">

      {/* Overview strip */}
      {Object.keys(allocations).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide">
            Active Restrictions
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(allocations).map(([key, alloc]) => {
              const [g, s] = key.split("|");
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white border border-amber-300 text-amber-800 font-medium"
                >
                  <Lock className="w-3 h-3" />
                  {g} · {s} — {alloc.allowed_topics.length} chapter{alloc.allowed_topics.length !== 1 ? "s" : ""}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Selectors row */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Grade pills */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Grade</p>
          <div className="flex flex-wrap gap-1.5">
            {grades.map(g => (
              <button
                key={g}
                onClick={() => { setSelectedGrade(g); setDirty(false); }}
                data-testid={`grade-pill-${g}`}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  selectedGrade === g
                    ? "bg-sky-600 text-white border-sky-600"
                    : "bg-white text-slate-600 border-slate-300 hover:border-sky-400"
                }`}
              >
                {g}
                {isRestricted(g, selectedSubject) && (
                  <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Subject pills */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Subject</p>
          <div className="flex flex-wrap gap-1.5">
            {subjects.map(s => (
              <button
                key={s}
                onClick={() => { setSelectedSubject(s); setDirty(false); }}
                data-testid={`subject-pill-${s}`}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  selectedSubject === s
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-slate-600 border-slate-300 hover:border-violet-400"
                }`}
              >
                {s}
                {isRestricted(selectedGrade, s) && (
                  <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Board pills — defaults to the school's own board; switching board doesn't
          lose selections already saved on other boards. */}
      {availableBoards.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Board</p>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setSelectedBoard("")}
              data-testid="board-pill-all"
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                !selectedBoard
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"
              }`}
            >
              All
            </button>
            {availableBoards.map(b => (
              <button
                key={b}
                onClick={() => setSelectedBoard(b)}
                data-testid={`board-pill-${b}`}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  selectedBoard === b
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400"
                }`}
              >
                {b}
                {b === schoolBoard && (
                  <span className="ml-1 text-[10px] opacity-75">(school)</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chapter checklist */}
      {selectedGrade && selectedSubject && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">
                {selectedSubject} — {selectedGrade}
              </span>
              {!loadingTopics && availableChapters.length > 0 && (
                <Badge
                  className={
                    visibleCheckedCount < availableChapters.length
                      ? "bg-amber-100 text-amber-700 border-amber-200"
                      : "bg-emerald-100 text-emerald-700 border-emerald-200"
                  }
                >
                  {visibleCheckedCount < availableChapters.length
                    ? `${visibleCheckedCount} of ${availableChapters.length} allocated`
                    : `All ${availableChapters.length} chapters`}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={selectAll}
                className="text-xs text-sky-600 hover:underline flex items-center gap-1"
                data-testid="select-all-btn"
              >
                <CheckSquare className="w-3.5 h-3.5" /> All
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={deselectAll}
                className="text-xs text-rose-500 hover:underline flex items-center gap-1"
                data-testid="deselect-all-btn"
              >
                <Square className="w-3.5 h-3.5" /> None
              </button>
            </div>
          </div>

          {/* Chapter rows */}
          <div className="divide-y divide-slate-100" data-testid="chapters-checklist">
            {loadingTopics ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : availableChapters.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                No chapters found for this grade + subject combination.
              </div>
            ) : (
              availableChapters.map((chapter, idx) => {
                const checked = checkedTopics.has(chapter);
                return (
                  <label
                    key={chapter}
                    className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors hover:bg-slate-50 ${
                      checked ? "" : "opacity-50"
                    }`}
                    data-testid={`chapter-row-${idx}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(chapter)}
                      data-testid={`chapter-checkbox-${idx}`}
                    />
                    <span className="text-sm text-slate-700 font-medium select-none">{chapter}</span>
                    {checked ? (
                      <Unlock className="w-3.5 h-3.5 text-emerald-500 ml-auto" />
                    ) : (
                      <Lock className="w-3.5 h-3.5 text-slate-300 ml-auto" />
                    )}
                  </label>
                );
              })
            )}
          </div>

          {/* Footer: save actions */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-500">
              {visibleCheckedCount} of {availableChapters.length} chapters selected
              {dirty && <span className="ml-2 text-amber-600 font-medium">· Unsaved changes</span>}
            </p>
            <div className="flex gap-2">
              {isRestricted(selectedGrade, selectedSubject) && !dirty && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await fetch(
                        `${API}/api/admin/cr/schools/${school.school_code}/topic-allocation?grade=${selectedGrade}&subject=${encodeURIComponent(selectedSubject)}`,
                        { method: "DELETE", headers: authHdr(token) },
                      );
                      toast.success("Restriction removed — all topics visible");
                      await loadAllocations();
                    } catch { toast.error("Failed to reset"); }
                    finally { setSaving(false); }
                  }}
                  className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  data-testid="reset-allocation-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to All
                </Button>
              )}
              <Button
                size="sm"
                onClick={save}
                disabled={!dirty || saving || loadingTopics}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="save-allocation-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                ) : (
                  <Save className="w-4 h-4 mr-1.5" />
                )}
                Save Allocation
              </Button>
            </div>
          </div>
        </div>
      )}

      {(!selectedGrade || !selectedSubject) && (
        <div className="text-center py-12 text-slate-400 text-sm">
          Select a grade and subject above to manage topic allocation.
        </div>
      )}
    </div>
  );
}
